import { useAIHelpContext, useFlag } from '@context'
import styled, { css } from 'styled-components'
import { motion } from 'framer-motion'
import { hideFeaturebase, useScreen } from '@utils'
import { SideSheet } from '@components'
import { AIHelpContent } from '@containers/AIHelpSheet/AIHelpContent.tsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocalStorage } from '@root/hooks'
import { MdDragIndicator } from 'react-icons/md'
import { AI_HELP_RESERVED_SPACE_CSS_VAR, AI_HELP_RESERVED_SPACE_EVENT } from '@constants/layout'

const DEFAULT_SHEET_RATIO = 0.25
const EXPANDED_SHEET_RATIO = 0.35
const MIN_SHEET_RATIO = 0.25
const MAX_SHEET_RATIO = 0.35

const getSheetBounds = (viewportWidth: number) => ({
  min: Math.round(viewportWidth * MIN_SHEET_RATIO),
  max: Math.round(viewportWidth * MAX_SHEET_RATIO)
})

const clampSheetWidth = (sheetWidth: number, viewportWidth: number) => {
  const { min, max } = getSheetBounds(viewportWidth)
  return Math.round(Math.min(max, Math.max(min, sheetWidth)))
}

export const AIHelpSheet: React.FC = () => {
  const { width: viewportWidth, isMobile } = useScreen()
  const { isAIHelpOpen, setAIHelpOpen, setInitialized } = useAIHelpContext()
  const useCustomAiWidget = useFlag('custom_ai_widget')
  const [sheetWidthPreference, setSheetWidthPreference] = useLocalStorage<number>(
    'aiHelp.sheetWidthPx',
    Math.round(viewportWidth * DEFAULT_SHEET_RATIO)
  )
  const [isResizing, setIsResizing] = useState(false)
  const isResizingRef = useRef(false)
  const isDockedSheet = !isMobile
  const sheetWidth = clampSheetWidth(
    sheetWidthPreference ?? Math.round(viewportWidth * DEFAULT_SHEET_RATIO),
    viewportWidth
  )
  const defaultSheetWidth = Math.round(viewportWidth * DEFAULT_SHEET_RATIO)
  const expandedSheetWidth = Math.round(viewportWidth * EXPANDED_SHEET_RATIO)
  const isExpanded = sheetWidth > (defaultSheetWidth + expandedSheetWidth) / 2

  const toggleSheetWidth = useCallback(() => {
    setSheetWidthPreference(prevWidth => {
      const currentWidth = clampSheetWidth(prevWidth ?? defaultSheetWidth, viewportWidth)
      const shouldCollapse = currentWidth > (defaultSheetWidth + expandedSheetWidth) / 2
      return shouldCollapse ? defaultSheetWidth : expandedSheetWidth
    })
  }, [defaultSheetWidth, expandedSheetWidth, setSheetWidthPreference, viewportWidth])

  const handleCloseAiHelp = useCallback(() => setAIHelpOpen(false), [setAIHelpOpen])

  const handleResize = useCallback(
    (event: MouseEvent) => {
      if (!isResizingRef.current || window.innerWidth <= 0) {
        return
      }

      const newWidth = window.innerWidth - event.clientX
      setSheetWidthPreference(clampSheetWidth(newWidth, window.innerWidth))
    },
    [setSheetWidthPreference]
  )

  const stopResizing = useCallback(() => {
    if (!isResizingRef.current) {
      return
    }

    isResizingRef.current = false
    setIsResizing(false)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
    window.removeEventListener('mousemove', handleResize)
    window.removeEventListener('mouseup', stopResizing)
  }, [handleResize])

  const startResizing = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (isResizingRef.current) {
        return
      }

      isResizingRef.current = true
      setIsResizing(true)
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
      window.addEventListener('mousemove', handleResize)
      window.addEventListener('mouseup', stopResizing)
    },
    [handleResize, stopResizing]
  )

  useEffect(() => {
    setInitialized(true)
    return () => setInitialized(false)
  }, [setInitialized])

  useEffect(() => {
    if (useCustomAiWidget.enabled && isAIHelpOpen) {
      hideFeaturebase()
    }
  }, [isAIHelpOpen, useCustomAiWidget.enabled])

  useEffect(() => {
    return () => {
      stopResizing()
    }
  }, [stopResizing])

  useEffect(() => {
    if (sheetWidthPreference !== sheetWidth) {
      setSheetWidthPreference(sheetWidth)
    }
  }, [setSheetWidthPreference, sheetWidth, sheetWidthPreference])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const reservedSpace = isDockedSheet && isAIHelpOpen ? `${sheetWidth}px` : '0px'
    document.documentElement.style.setProperty(AI_HELP_RESERVED_SPACE_CSS_VAR, reservedSpace)
    window.dispatchEvent(new Event(AI_HELP_RESERVED_SPACE_EVENT))

    return () => {
      document.documentElement.style.setProperty(AI_HELP_RESERVED_SPACE_CSS_VAR, '0px')
      window.dispatchEvent(new Event(AI_HELP_RESERVED_SPACE_EVENT))
    }
  }, [isDockedSheet, isAIHelpOpen, sheetWidth])

  return isDockedSheet ? (
    <StyledSideSheet
      initial={false}
      animate={{ width: isAIHelpOpen ? sheetWidth : 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      style={{
        pointerEvents: isAIHelpOpen ? 'auto' : 'none',
        backgroundColor: isAIHelpOpen ? undefined : 'transparent'
      }}
      aria-hidden={!isAIHelpOpen}
      /* The closed sheet keeps its full-width content mounted next to the layout. Without inert,
         tabbing reaches the off-screen chat iframe and the browser scrolls the whole app sideways
         to reveal it. */
      inert={!isAIHelpOpen}
    >
      <ResizeHandle
        type="button"
        onMouseDown={startResizing}
        aria-label="Resize AI chat"
        aria-hidden={!isAIHelpOpen}
        tabIndex={isAIHelpOpen ? 0 : -1}
        $isOpen={isAIHelpOpen}
        $isResizing={isResizing}
      >
        <ResizeHandleIcon />
      </ResizeHandle>
      <ContentWrapper
        $isResizing={isResizing}
        initial={false}
        animate={{ width: sheetWidth, opacity: isAIHelpOpen ? 1 : 0 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        {isResizing && <ResizeOverlay aria-hidden />}
        <AIHelpContent
          removeDividerSideRadius={isAIHelpOpen}
          onToggleEmbedWidth={toggleSheetWidth}
          onClose={handleCloseAiHelp}
          isExpanded={isExpanded}
        />
      </ContentWrapper>
    </StyledSideSheet>
  ) : (
    <SideSheet isShown={isAIHelpOpen} handleClose={handleCloseAiHelp} hideHeader keepMounted>
      <AIHelpContent onClose={handleCloseAiHelp} />
    </SideSheet>
  )
}

const ContentWrapper = styled(motion.div)<{ $isResizing: boolean }>`
  position: relative;
  height: 100%;
  cursor: ${({ $isResizing }) => ($isResizing ? 'col-resize' : 'default')};
`

export const StyledSideSheet = styled(motion.div).attrs({
  'data-ai-help-sheet': 'true'
})`
  position: relative;
  z-index: 10000000;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.menuBg};
  min-width: 0;
  overflow: visible;
`

const ResizeHandle = styled.button<{ $isOpen: boolean; $isResizing: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  transform: translateX(-50%);
  width: 30px;
  height: 100%;
  border: none;
  padding: 0;
  background: transparent;
  z-index: 8;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  outline: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.neutralGray};
    box-shadow: none;
    opacity: 0.9;
    cursor: col-resize;
    transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, width 0.2s ease;
  }

  &::after {
    content: none;
  }

  &:hover::before,
  &:focus-visible::before,
  &:active::before {
    width: 8px;
    background: ${({ theme }) => theme.colors.menuBg};
    opacity: 1;
  }

  &:hover svg,
  &:focus-visible svg,
  &:active svg {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.03);
  }

  ${({ $isResizing, theme }) =>
    $isResizing &&
    css`
      &::before {
        width: 8px;
        background: ${theme.colors.neutralGray};
        opacity: 1;
      }

      svg {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1.03);
      }
    `}
`

const ResizeHandleIcon = styled(MdDragIndicator)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.neutralWhite};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
`

const ResizeOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: col-resize;
  background: transparent;
`
